import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Sparkles,
  MessageSquare,
  Pencil,
  FileText,
  Mic,
  ArrowUp,
  BarChart3,
  Mail,
  AlertCircle,
} from 'lucide-react';
import WorkflowUtilityPanel from './WorkflowUtilityPanel';
import { cn } from '../../lib/utils';

const BUILD_ACTIONS = [
  {
    id: 'edit',
    title: 'Edit this workflow',
    description: 'Tell AI what to modify',
    icon: Pencil,
    prompt: 'Edit this workflow: ',
  },
  {
    id: 'describe',
    title: 'Describe this workflow',
    description: 'Walk through what this workflow does',
    icon: FileText,
    prompt: 'Describe this workflow step by step.',
  },
];

const CHAT_STARTERS = [
  {
    id: 'metrics',
    title: 'Key metrics at a glance',
    description: 'How is this workflow performing?',
    icon: BarChart3,
    prompt: 'How is this workflow performing?',
  },
  {
    id: 'delivery',
    title: 'Opens, clicks, and delivery rates',
    description: 'Show me email & SMS analytics',
    icon: Mail,
    prompt: 'Show me email and SMS analytics for this workflow.',
  },
  {
    id: 'error',
    title: 'Help with an error',
    description: "I'm getting an error that says...",
    icon: AlertCircle,
    prompt: "I'm getting an error that says ",
  },
  {
    id: 'explain',
    title: 'Explain this workflow',
    description: 'Walk me through what this workflow does step by step',
    icon: FileText,
    prompt: 'Walk me through what this workflow does step by step.',
  },
];

const WorkflowAIPanel = ({
  apiBase,
  token,
  workflow,
  onClose,
  onApplyDraft,
}) => {
  const [tab, setTab] = useState('build');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');

  const placeholderResponse = 'Workflow AI is being prepared. Try again shortly.';

  const submit = async (promptText) => {
    const prompt = (promptText || input).trim();
    if (!prompt) return;

    setLoading(true);
    setResponse('');
    try {
      if (tab === 'build') {
        const res = await axios.post(
          `${apiBase}/generate-from-prompt`,
          { prompt, scope: 'platform' },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const draft = res.data?.draft;
        if (draft) {
          onApplyDraft?.(draft);
          setResponse('Draft applied to your workflow. Review changes and save when ready.');
        } else {
          setResponse(placeholderResponse);
        }
      } else {
        setResponse(placeholderResponse);
      }
    } catch {
      setResponse(placeholderResponse);
    } finally {
      setLoading(false);
    }
  };

  const title = tab === 'build' ? 'What would you like to change?' : 'How can I help you?';
  const placeholder = tab === 'build' ? 'Describe changes to your workflow...' : 'Ask a question...';
  const quickItems = useMemo(() => (tab === 'build' ? BUILD_ACTIONS : CHAT_STARTERS), [tab]);

  return (
    <WorkflowUtilityPanel
      title=""
      onClose={onClose}
      testId="workflow-ai-panel"
      headerActions={null}
      className="[&>div:first-child]:border-none [&>div:first-child]:pb-0"
    >
      <div className="-mt-2 mb-4 flex items-center justify-center gap-1 rounded-lg bg-slate-100 p-1">
        <button
          type="button"
          onClick={() => setTab('build')}
          className={cn(
            'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
            tab === 'build' ? 'bg-violet-100 text-violet-700' : 'text-slate-500',
          )}
          data-testid="workflow-ai-tab-build"
        >
          <Sparkles size={14} /> Build
        </button>
        <button
          type="button"
          onClick={() => setTab('chat')}
          className={cn(
            'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
            tab === 'chat' ? 'bg-violet-100 text-violet-700' : 'text-slate-500',
          )}
          data-testid="workflow-ai-tab-chat"
        >
          <MessageSquare size={14} /> Chat
        </button>
      </div>

      <div className="flex flex-col items-center text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-600 text-white shadow-md">
          <Sparkles size={24} />
        </div>
        <h3 className="mb-4 text-lg font-semibold text-slate-900">{title}</h3>

        <div className="relative w-full">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            rows={4}
            className="w-full resize-none rounded-xl border border-slate-200 px-3 py-3 pr-20 text-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
            data-testid="workflow-ai-input"
          />
          <div className="absolute bottom-2 right-2 flex items-center gap-1">
            <button type="button" className="rounded p-1.5 text-slate-400" aria-label="Voice input placeholder">
              <Mic size={16} />
            </button>
            <button
              type="button"
              onClick={() => submit()}
              disabled={loading}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              aria-label="Submit"
            >
              <ArrowUp size={16} />
            </button>
          </div>
        </div>

        <div className="mt-5 w-full space-y-2 text-left">
          {quickItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setInput(item.prompt);
                  submit(item.prompt);
                }}
                className="flex w-full items-start gap-3 rounded-lg border border-slate-100 px-3 py-2.5 text-left hover:bg-slate-50"
              >
                <Icon size={16} className="mt-0.5 text-violet-500" />
                <span>
                  <span className="block text-sm font-medium text-slate-900">{item.title}</span>
                  <span className="block text-xs text-slate-500">{item.description}</span>
                </span>
              </button>
            );
          })}
        </div>

        {response && (
          <p className="mt-4 w-full rounded-lg bg-slate-50 px-3 py-2 text-left text-sm text-slate-600">
            {response}
          </p>
        )}
        {loading && <p className="mt-3 text-sm text-slate-500">Thinking...</p>}
      </div>
    </WorkflowUtilityPanel>
  );
};

export default WorkflowAIPanel;
